import { useState } from "react";
import PropTypes from "prop-types";
import { Trash } from "react-bootstrap-icons";

import useSwipeToReveal from "../../../hooks/useSwipeToReveal";
import Money from "../../../components/Money";
import { useHouseDispatch } from "../../../contexts/HouseContext";
import {
    PAYMENT_METHODS,
    MethodGlyph,
    methodClass,
    paymentLabel,
} from "./paymentMethods";

import "./TransactionDetail.css";

/**
 * One transaction row inside the expanded `.tx-details` panel.
 * Layout mirrors `.tx-line` in the mockup:
 *   [method-chip] [meta] [settled] [buy-in] [cash-out] [running-net]
 *
 * Swipe the row left (touch or mouse drag) to reveal a delete action;
 * tap it to remove the transaction. Tapping the row closes it again.
 */
export function TransactionDetail({ gameId, playerId, transaction }) {
    const actions = useHouseDispatch();
    const [showPicker, setShowPicker] = useState(false);
    const { offset, dragging, revealed, handlers } = useSwipeToReveal();

    const isBuyIn = transaction.type === "BUY_IN" || transaction.type === "ADD_ON";
    const buyInAmt = isBuyIn ? transaction.amount : null;
    const cashOutAmt = !isBuyIn ? transaction.amount : null;
    // This transaction's own effect on the player's net — buy-ins out
    // (negative), cash-outs in (positive). Not a running cumulative.
    const txnNet = isBuyIn ? -transaction.amount : transaction.amount;

    const netClass = txnNet > 0 ? "is-up" : txnNet < 0 ? "is-down" : "";

    const toggleSettled = async (e) => {
        e.stopPropagation();
        await actions.toggleTransactionStatus(gameId, playerId, {
            ...transaction,
            isSettled: !transaction.isSettled,
        });
    };

    const handleDelete = async () => {
        await actions.deleteTransaction(gameId, playerId, transaction);
    };

    const setPaymentType = async (newType) => {
        setShowPicker(false);
        await actions.changeTransaction(gameId, playerId, {
            ...transaction,
            paymentType: newType,
        });
    };

    return (
        <div className="tx-row">
            <div className="tx-swipe">
                <button
                    type="button"
                    className="tx-swipe-delete"
                    onClick={handleDelete}
                    aria-label="Delete transaction"
                    tabIndex={revealed ? 0 : -1}
                >
                    <Trash aria-hidden="true" />
                </button>
                <div
                    className={`tx-line${dragging ? " is-dragging" : ""}`}
                    style={{ transform: `translateX(${offset}px)` }}
                    {...handlers}
                >
                    <button
                        className={`tx-method ${methodClass(transaction.paymentType)}`}
                        title={paymentLabel(transaction.paymentType)}
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowPicker((v) => !v);
                        }}
                    >
                        <MethodGlyph type={transaction.paymentType} />
                    </button>
                    <div className="tx-meta">
                        <span className="tx-meta-text">
                            <span className="label">{transactionTypeLabel(transaction.type)}</span>
                            <span className="ts">{formatTs(transaction.time ?? transaction.createdAt)}</span>
                        </span>
                        <button
                            type="button"
                            className={`tx-state ${transaction.isSettled ? "is-settled" : "is-pending"}`}
                            onClick={toggleSettled}
                            aria-pressed={transaction.isSettled}
                            title={transaction.isSettled ? "Mark unsettled" : "Mark settled"}
                        >
                            {transaction.isSettled ? "Settled" : "Pending"}
                        </button>
                    </div>
                    <div className={`num col-buyin${buyInAmt == null ? " dim" : ""}`}>
                        {buyInAmt != null ? <Money amount={buyInAmt} /> : "—"}
                    </div>
                    <div className={`num col-cashout${cashOutAmt == null ? " dim" : ""}`}>
                        {cashOutAmt != null ? <Money amount={cashOutAmt} /> : "—"}
                    </div>
                    <div className={`num ${netClass}`}>
                        <Money amount={txnNet} signed={txnNet !== 0} />
                    </div>
                </div>
            </div>
            {showPicker && <PaymentPicker onPick={setPaymentType} />}
        </div>
    );
}

const PaymentPicker = ({ onPick }) => (
    <div className="payment-type-picker">
        {PAYMENT_METHODS.map((m) => (
            <button
                key={m.type}
                className="payment-option"
                onClick={() => onPick(m.type)}
                title={m.label}
            >
                <MethodGlyph type={m.type} />
            </button>
        ))}
    </div>
);

const TRANSACTION_TYPE_LABELS = {
    BUY_IN: "Buy-in",
    ADD_ON: "Add-on",
    CASH_OUT: "Cash-out",
    COVER: "Cover",
};

function transactionTypeLabel(type) {
    return TRANSACTION_TYPE_LABELS[type] ?? type;
}

function formatTs(ts) {
    if (!ts) return "";
    try {
        return new Date(ts).toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
        });
    } catch {
        return "";
    }
}

TransactionDetail.propTypes = {
    gameId: PropTypes.string,
    playerId: PropTypes.string,
    transaction: PropTypes.object,
};
